import React, { useCallback, memo } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { getIn, toJS } from '../../utils/ChangeSpy';
import {
  findCompanies,
  findProjects,
  changeField,
  clearSuggest,
  addContactFund,
  closeValidationError,
} from '../../actions/contact/contactExecutive';
import Popups from '../../components/Popup';
import debounce from '../../utils/debounce';
import AutoCompleteContainer from '../AutoComplete';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const renderProjectSuggestion = suggestion => {
  const className = classNames({ 'text-muted': !suggestion.isActive });

  return <span className={className}>{suggestion.text}</span>;
};

const AddFundPopupContainer = ({
  children,
  executive,
  newFund,
  popup,
  contactId,
  inputErrors,
  findCompanies,
  findProjects,
  changeField,
  addContactFund,
  closeValidationError,
}) => {
  const context = usePopupContext();
  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleClose = useCallback(() => {
    context.closePopup();
  }, [context.closePopup]);

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      if (!name) return;
      changeField({ name, value });
    },
    [changeField],
  );

  const handleCreate = useCallback(() => {
    const fundFormData = toJS(executive.get('newFund').filter(field => field !== null));

    if (!fundFormData.eprojectId || fundFormData.eprojectId <= 0) {
      delete fundFormData.eprojectId;
    }
    addContactFund({ ...fundFormData, id: contactId }, handleClose);
  }, [executive, contactId, addContactFund, handleClose]);

  const handleFetchCompanies = useCallback(
    debounce(({ ...rest }) => {
      findCompanies(rest);
    }),
    [findCompanies],
  );

  const handleFetchProjects = useCallback(
    debounce(({ ...rest }) => {
      const fundId = getIn(executive, ['newFund', 'fundId'], 0);

      findProjects({ fundId, ...rest });
    }),
    [findProjects, executive],
  );

  const handleUpdateCompany = useCallback(
    param => {
      changeField(param);
    },
    [changeField],
  );

  const handleUpdateProject = useCallback(
    param => {
      changeField(param);
    },
    [changeField],
  );

  const suggestCompany = (
    <AutoCompleteContainer
      idName="newFund.fundId"
      idValue={getIn(executive, 'newFund.fundId')}
      inputProps={{
        label: 'Company',
        name: 'newFund.suggestCompany',
        className: 'form-control input-sm',
      }}
      onChange={handleUpdateCompany}
      onFind={handleFetchCompanies}
      suggestions={getIn(executive, 'suggests')}
      suggestionsName="suggests"
      value={getIn(executive, 'newFund.suggestCompany', '')}
      valueName="newFund.suggestCompany"
    />
  );

  const disabled = getIn(executive, 'newFund.suggestCompany', '') === '';

  const suggestProject = (
    <AutoCompleteContainer
      idName="newFund.eprojectId"
      idValue={getIn(executive, 'newFund.eprojectId', null)}
      inputProps={{
        label: 'Project',
        name: 'newFund.suggestProject',
        disabled,
        className: 'form-control input-sm',
      }}
      onChange={handleUpdateProject}
      onFind={handleFetchProjects}
      render={renderProjectSuggestion}
      suggestions={getIn(executive, 'suggests')}
      suggestionsName="suggests"
      value={getIn(executive, 'newFund.suggestProject', '')}
      valueName="newFund.suggestProject"
      suggestIfEmpty
    />
  );

  const statuses = popup.getIn(['props', 'statuses']);
  const isValid = inputErrors && inputErrors.size === 0;

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <div>
        <Popups.AddFundPopup
          executive={executive}
          fund={newFund}
          isValid={isValid}
          onChange={handleChange}
          onClose={handleClose}
          onCreate={handleCreate}
          statuses={statuses}
          suggestCompany={suggestCompany}
          suggestProject={suggestProject}
        />
        {children}
      </div>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    contactId: state.contact.info.get('id'),
    inputErrors: state.contact.executive.get('newFundInputErrors', Map()),
    executive: state.contact.executive,
    newFund: state.contact.executive.get('newFund'),
  };
}

export { AddFundPopupContainer };
export default connect(
  mapStateToProps,
  {
    findCompanies,
    findProjects,
    changeField,
    clearSuggest,
    addContactFund,
    closeValidationError,
  },
  mergeProps,
  connectOptions,
)(memo(AddFundPopupContainer));
